import { motion } from "framer-motion";
import styles from "./liquid-logo.module.css";

interface LiquidLogoProps {
  className?: string;
  height?: number | string;
  opacity?: number;
}

export function LiquidLogo({
  className,
  height = "100%",
  opacity = 1,
}: LiquidLogoProps) {
  return (
    <div
      className={`${styles.container} ${className || ""}`}
      style={{ height, opacity }}
    >
      <svg
        viewBox="0 0 631 195"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.svg}
      >
        <defs>
          <linearGradient
            id="shimmer-admin"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <motion.stop
              offset="0%"
              stopColor="#8e8e8e"
              animate={{
                stopColor: ["#8e8e8e", "#ffffff", "#c0c0c0", "#8e8e8e"],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.stop
              offset="25%"
              stopColor="#ffffff"
              animate={{
                stopColor: ["#ffffff", "#c0c0c0", "#8e8e8e", "#ffffff"],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.stop
              offset="50%"
              stopColor="#c0c0c0"
              animate={{
                stopColor: ["#c0c0c0", "#8e8e8e", "#ffffff", "#c0c0c0"],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.stop
              offset="75%"
              stopColor="#8e8e8e"
              animate={{
                stopColor: ["#8e8e8e", "#ffffff", "#c0c0c0", "#8e8e8e"],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.stop
              offset="100%"
              stopColor="#ffffff"
              animate={{
                stopColor: ["#ffffff", "#c0c0c0", "#8e8e8e", "#ffffff"],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Liquid highlight overlay - more intense */}
            <motion.stop
              offset="0%"
              stopColor="white"
              stopOpacity="1"
              animate={{ offset: ["-50%", "150%"] }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 1,
                ease: "linear",
              }}
            />
          </linearGradient>
          <mask id="logo-mask-admin">
            <path
              d="M105.502 154.217C99.8287 171.932 89.1346 184.789 71.7466 191.36C59.1342 196.095 46.3993 195.932 33.7868 191.116C8.64357 181.442 -5.27502 154.339 1.86795 128.38C5.8272 113.89 14.6028 102.951 27.8684 95.8079C41.0522 88.7057 54.9708 87.5629 69.7466 91.5221C56.93 77.5219 51.0932 61.5625 54.5218 42.8275C56.8484 30.1334 63.2159 19.7251 73.2976 11.7249C93.8694 -4.68349 123.094 -3.78551 142.523 13.8882C153.666 24.0108 159.421 36.8274 159.748 51.8072C160.075 66.787 154.238 79.5627 143.135 91.2364C160.442 87.3996 175.422 89.8486 188.891 99.4814C199.545 107.073 206.524 117.359 209.463 130.094C215.545 156.544 200.891 182.993 175.503 191.769C165.911 195.075 156.156 195.728 146.238 193.524C136.36 191.32 127.707 186.626 120.441 179.564C113.257 172.544 108.686 163.972 105.502 154.217ZM146.482 53.1133C146.646 31.3171 129.094 13.3984 107.298 13.1127C85.1754 12.827 66.8486 30.4599 66.5628 52.3378C66.2771 74.1341 84.2774 92.0936 106.482 92.216C128.482 92.3384 146.278 74.9504 146.442 53.1133H146.482ZM117.911 141.482C117.625 163.36 135.054 181.319 156.891 181.687C179.136 182.054 197.381 164.707 197.871 142.788C198.361 121.278 180.483 102.951 158.728 102.624C136.319 102.257 118.155 119.523 117.911 141.523V141.482ZM93.0531 142.38C93.2571 120.584 75.7467 102.869 53.7055 102.543C31.5419 102.257 13.1743 119.972 13.0518 141.768C12.9294 163.319 30.848 181.442 52.5626 181.768C74.6446 182.095 92.8898 164.34 93.0939 142.38H93.0531ZM90.1551 103.481C97.3388 111.237 102.278 120.053 105.543 130.053C108.523 120.216 113.502 111.808 120.074 104.135C109.87 106.461 100.155 105.645 90.1551 103.481Z"
              fill="white"
            />
            <path
              d="M250.141 88.125V34.0425H265.57L281.611 72.1655H281.897L297.856 34.0425H313.203V88.125H301.938V49.9203H301.652L286.182 88.125H276.509L261.08 49.9203H260.794V88.125H250.182H250.141Z"
              fill="white"
            />
            <path
              d="M319.244 88.125L342.142 34.0425H353.775L376.388 88.125H364.755L359.204 74.9003H336.102L330.428 88.125H319.203H319.244ZM339.653 65.4307H356.143L348.061 45.9202H347.734L339.653 65.4307Z"
              fill="white"
            />
            <path
              d="M403.164 88.9412C395.899 88.9412 390.103 87.3493 385.817 84.1656C381.531 80.9819 379.123 76.5737 378.755 70.9001H390.388C390.796 73.8389 392.103 76.043 394.347 77.5533C396.592 79.0635 399.694 79.7982 403.654 79.7982C407.164 79.7982 409.817 79.2268 411.613 78.0839C413.409 76.941 414.266 75.2675 414.266 73.0226C414.266 71.3083 413.695 69.9205 412.552 68.9001C411.409 67.8796 409.572 67.1041 407.042 66.5735L395.898 64.2877C390.878 63.2265 387.123 61.4714 384.633 58.9815C382.143 56.4917 380.878 53.308 380.878 49.3487C380.878 44.3282 382.796 40.369 386.592 37.5526C390.388 34.7363 395.572 33.2668 402.103 33.2668C408.633 33.2668 414.062 34.7771 418.144 37.7567C422.185 40.7364 424.47 44.8997 424.96 50.2467H413.327C412.021 44.8181 408.103 42.0833 401.531 42.0833C398.552 42.0833 396.225 42.6139 394.511 43.6752C392.837 44.7364 391.98 46.2875 391.98 48.2875C391.98 51.3488 394.184 53.3488 398.592 54.2468L410.225 56.655C415.491 57.7162 419.327 59.5122 421.736 62.0428C424.144 64.5735 425.368 67.9613 425.368 72.2062C425.368 77.5124 423.45 81.635 419.572 84.5738C415.695 87.5126 410.266 88.982 403.164 88.982V88.9412Z"
              fill="white"
            />
            <path
              d="M456.185 88.125H445.083V43.9202H427.531V34.0425H473.736V43.9202H456.185V88.125Z"
              fill="white"
            />
            <path
              d="M520.472 88.125H481.002V34.0425H520.472V43.9202H492.308V55.2673H518.268V64.9001H492.308V78.2473H520.472V88.125Z"
              fill="white"
            />
            <path
              d="M529.452 88.125V34.0425H557.411C563.207 34.0425 567.738 35.3486 571.003 37.9609C574.269 40.5732 575.901 44.1651 575.901 48.7366C575.901 51.6346 575.208 54.0836 573.861 56.1245C572.514 58.1653 570.595 59.6347 568.146 60.4511V60.7776C570.595 60.7776 572.514 61.4307 573.861 62.7368C575.208 64.043 575.901 66.0022 575.901 68.6553V88.0841H564.554V67.5124H540.717V88.0841H529.452V88.125ZM540.717 57.7571H556.677C559.248 57.7571 561.248 57.1449 562.636 55.9612C564.024 54.7775 564.718 53.0632 564.718 50.8183C564.718 48.5733 564.024 46.8998 562.636 45.6753C561.248 44.4916 559.248 43.8794 556.677 43.8794H540.717V57.7571Z"
              fill="white"
            />
            <path
              d="M607.861 88.9412C600.596 88.9412 594.8 87.3493 590.514 84.1656C586.228 80.9819 583.82 76.5737 583.452 70.9001H595.085C595.493 73.8389 596.8 76.043 599.045 77.5533C601.289 79.0635 604.392 79.7982 608.351 79.7982C611.861 79.7982 614.514 79.2268 616.31 78.0839C618.106 76.941 618.963 75.2675 618.963 73.0226C618.963 71.3083 618.392 69.9205 617.249 68.9001C616.106 67.8796 614.269 67.1041 611.739 66.5735L600.596 64.2877C595.575 63.2265 591.82 61.4714 589.33 58.9815C586.84 56.4917 585.575 53.308 585.575 49.3487C585.575 44.3282 587.493 40.369 591.289 37.5526C595.085 34.7363 600.269 33.2668 606.8 33.2668C613.33 33.2668 618.759 34.7771 622.841 37.7567C626.882 40.7364 629.167 44.8997 629.657 50.2467H618.024C616.718 44.8181 612.8 42.0833 606.228 42.0833C603.249 42.0833 600.922 42.6139 599.208 43.6752C597.534 44.7364 596.677 46.2875 596.677 48.2875C596.677 51.3488 598.881 53.3488 603.289 54.2468L614.922 56.655C620.188 57.7162 624.024 59.5122 626.433 62.0428C628.841 64.5735 630.065 67.9613 630.065 72.2062C630.065 77.5124 628.147 81.635 624.269 84.5738C620.433 87.5126 614.963 88.982 607.861 88.982V88.9412Z"
              fill="white"
            />
            <path
              d="M274.999 161.555C269.488 161.555 264.672 160.412 260.549 158.126C256.427 155.84 253.243 152.575 250.957 148.371C248.672 144.167 247.57 139.269 247.57 133.636C247.57 128.003 248.712 122.983 250.957 118.819C253.243 114.656 256.427 111.391 260.549 109.105C264.672 106.819 269.488 105.676 274.999 105.676C279.448 105.676 283.407 106.452 286.836 107.962C290.264 109.513 293.121 111.676 295.325 114.534C297.53 117.391 298.917 120.779 299.489 124.738H287.733C287.244 121.84 285.856 119.595 283.611 118.044C281.325 116.452 278.468 115.676 274.999 115.676C270.019 115.676 266.06 117.268 263.202 120.411C260.304 123.554 258.876 127.962 258.876 133.636C258.876 139.309 260.304 143.636 263.202 146.82C266.1 150.004 270.019 151.595 274.999 151.595C278.468 151.595 281.325 150.779 283.611 149.187C285.897 147.595 287.244 145.35 287.733 142.452H299.489C298.917 146.412 297.53 149.84 295.325 152.697C293.121 155.555 290.305 157.759 286.836 159.269C283.366 160.779 279.448 161.555 274.999 161.555Z"
              fill="white"
            />
            <path
              d="M306.101 160.575V106.492H317.367V150.697H345.204V160.575H306.101Z"
              fill="white"
            />
            <path
              d="M374.47 161.35C369.612 161.35 365.408 160.616 361.775 159.106C358.184 157.595 355.408 155.31 353.449 152.248C351.49 149.187 350.51 145.187 350.51 140.411V106.492H362.061V138.819C362.061 142.615 363.082 145.554 365.082 147.677C367.123 149.799 370.347 150.86 374.796 150.86C379.245 150.86 382.592 149.758 384.592 147.513C386.551 145.269 387.572 142.411 387.572 138.901V106.533H398.837V140.575C398.837 147.513 396.674 152.738 392.347 156.208C388.021 159.677 382.062 161.432 374.47 161.432V161.35Z"
              fill="white"
            />
            <path
              d="M409.572 160.575V106.492H437.328C440.593 106.492 443.41 107.064 445.859 108.248C448.308 109.431 450.226 111.023 451.614 113.105C453.002 115.186 453.695 117.554 453.695 120.289C453.695 123.023 453.001 125.309 451.573 127.187C450.144 129.064 448.226 130.493 445.777 131.432V131.717C447.859 132.248 449.695 133.146 451.287 134.37C452.879 135.595 454.104 137.146 455.042 138.983C455.94 140.819 456.43 142.86 456.43 145.187C456.43 148.248 455.655 150.942 454.144 153.269C452.634 155.595 450.553 157.391 447.899 158.657C445.246 159.963 442.144 160.575 438.593 160.575H409.613H409.572ZM420.838 128.044H435.736C437.858 128.044 439.491 127.554 440.634 126.534C441.777 125.513 442.348 124.085 442.348 122.166C442.348 120.248 441.777 118.819 440.634 117.84C439.491 116.86 437.858 116.37 435.736 116.37H420.838V128.044ZM420.838 150.656H436.511C439.246 150.656 441.328 150.085 442.797 148.901C444.267 147.718 445.001 146.003 445.001 143.799C445.001 141.595 444.267 139.881 442.838 138.738C441.409 137.595 439.328 136.983 436.593 136.983H420.838V150.656Z"
              fill="white"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="631"
          height="195"
          fill="url(#shimmer-admin)"
          mask="url(#logo-mask-admin)"
        />
      </svg>
    </div>
  );
}
